import { api } from './client';
import i18n from '@/i18n';
import type {
  ActivityView,
  CategoryNode,
  CategoryView,
  ChangePasswordRequest,
  CurrentUser,
  DashboardStats,
  DeleteAccountRequest,
  ForgotPasswordRequest,
  ItemSummary,
  ItemView,
  LocationNode,
  LocationPhotoView,
  LocationView,
  LoginResponse,
  Page,
  PhotoView,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  TagView,
  UpdateProfileRequest,
} from '@/types/api';

export const auth = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }).then((r) => r.data),
  register: (body: RegisterRequest) =>
    api.post<RegisterResponse>('/auth/register', body).then((r) => r.data),
  me: () => api.get<CurrentUser>('/auth/me').then((r) => r.data),
  updateProfile: (body: UpdateProfileRequest) =>
    api.patch<CurrentUser>('/auth/me', body).then((r) => r.data),
  // A wrong current password comes back as a 401; that's an expected, inline-recoverable
  // outcome here (not an invalid session), so this opts out of the global 401 handler.
  changePassword: (body: ChangePasswordRequest) =>
    api.post('/auth/change-password', body, { skipAuthRedirect: true }).then(() => undefined),
  // Same 401-handling rationale as changePassword: a wrong confirmation password
  // is an inline-recoverable outcome, not an invalid session.
  deleteAccount: (body: DeleteAccountRequest) =>
    api.delete('/auth/me', { data: body, skipAuthRedirect: true }).then(() => undefined),
  // Always resolves 202 regardless of whether the email is registered (anti-account-
  // enumeration by design) -- callers must show the same message either way and only
  // need to branch on network/5xx failures, not on "email not found".
  // The base i18next language (e.g. "tr", not "tr-TR") is attached so the reset email
  // is sent in whatever language the web UI is currently showing (issue #148); the
  // backend whitelists this to tr/en and falls back to tr for anything else.
  forgotPassword: (body: ForgotPasswordRequest) =>
    api
      .post('/auth/forgot-password', { ...body, language: i18n.language?.split('-')[0] })
      .then(() => undefined),
  // An invalid/expired/used token comes back as a 400 with the same generic message
  // as any other reason -- this is an unauthenticated call, so skipAuthRedirect isn't
  // relevant here (no token in localStorage to begin with on this page).
  resetPassword: (body: ResetPasswordRequest) =>
    api.post('/auth/reset-password', body).then(() => undefined),
};

export const locations = {
  tree: () => api.get<LocationNode[]>('/locations/tree').then((r) => r.data),
  list: (parentId?: number | null) =>
    api
      .get<LocationView[]>('/locations', { params: parentId == null ? {} : { parentId } })
      .then((r) => r.data),
  get: (id: number) => api.get<LocationView>(`/locations/${id}`).then((r) => r.data),
  create: (body: Partial<LocationView>) => api.post<LocationView>('/locations', body).then((r) => r.data),
  update: (id: number, body: Partial<LocationView>) =>
    api.put<LocationView>(`/locations/${id}`, body).then((r) => r.data),
  move: (id: number, newParentId: number | null) =>
    api.post<LocationView>(`/locations/${id}/move`, { newParentId }).then((r) => r.data),
  remove: (id: number) => api.delete(`/locations/${id}`).then(() => undefined),
  // A location has a single cover photo (not a gallery), so uploading again
  // just replaces it — mirrors the backend's singular "/photo" sub-resource.
  uploadPhoto: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<LocationPhotoView>(`/locations/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  // Same raw-URL-string convention as LocationView.coverPhotoUrl / ItemSummary.thumbnailPath;
  // handed straight to AuthedImage, which fetches it with the bearer token attached.
  photoRawUrl: (id: number) => `/api/locations/${id}/photo/raw`,
  removePhoto: (id: number) => api.delete(`/locations/${id}/photo`).then(() => undefined),
};

export const categories = {
  tree: () => api.get<CategoryNode[]>('/categories/tree').then((r) => r.data),
  list: () => api.get<CategoryView[]>('/categories').then((r) => r.data),
};

export const tags = {
  list: () => api.get<TagView[]>('/tags').then((r) => r.data),
  create: (name: string, color?: string | null) =>
    api.post<TagView>('/tags', { name, color }).then((r) => r.data),
  remove: (id: number) => api.delete(`/tags/${id}`).then(() => undefined),
};

export interface ItemSearchParams {
  q?: string;
  locationId?: number;
  categoryId?: number;
  tagIds?: number[];
  page?: number;
  size?: number;
  /** Passed straight through to Spring's Pageable, e.g. 'createdAt,desc'. */
  sort?: string;
}

export const items = {
  search: (params: ItemSearchParams) =>
    api.get<Page<ItemSummary>>('/items', { params }).then((r) => r.data),
  get: (id: number) => api.get<ItemView>(`/items/${id}`).then((r) => r.data),
  create: (body: Partial<ItemView> & { tagIds?: number[] }) =>
    api.post<ItemView>('/items', body).then((r) => r.data),
  update: (id: number, body: Partial<ItemView> & { tagIds?: number[] }) =>
    api.put<ItemView>(`/items/${id}`, body).then((r) => r.data),
  move: (id: number, newLocationId: number) =>
    api.post<ItemView>(`/items/${id}/move`, { newLocationId }).then((r) => r.data),
  remove: (id: number) => api.delete(`/items/${id}`).then(() => undefined),
  uploadPhoto: (id: number, file: File, type?: 'ITEM' | 'RECEIPT') => {
    const fd = new FormData();
    fd.append('file', file);
    // Omit the field entirely for plain item photos — the backend already
    // defaults to ITEM when `type` isn't present.
    if (type) fd.append('type', type);
    return api
      .post<PhotoView>(`/items/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  removePhoto: (id: number, photoId: number) =>
    api.delete(`/items/${id}/photos/${photoId}`).then(() => undefined),
  stats: () => api.get<DashboardStats>('/items/stats').then((r) => r.data),
};

export const activity = {
  recent: (size = 10) =>
    api.get<Page<ActivityView>>('/activity', { params: { size } }).then((r) => r.data),
};

export const exportApi = {
  url: '/api/export',
};
